import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { RemovePermissionsConfirmDialog } from '@/components/roles/RemovePermissionsConfirmDialog';
import { RoleForm, type RoleFormValues } from '@/components/roles/RoleForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { getRoleRequest, updateRoleRequest } from '@/services/roles';
import type { RoleDetail, UpdateRoleApiResponse, UpdateRoleBody } from '@/types/roles';

interface PendingValues {
  readonly values: RoleFormValues;
  readonly removingCodes: readonly string[];
}

export function RoleEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingValues | null>(null);

  const detailQuery = useQuery<RoleDetail, ApiError>({
    queryKey: ['role', id],
    queryFn: () => getRoleRequest(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  const updateMutation = useMutation<
    UpdateRoleApiResponse,
    ApiError,
    { id: number; body: UpdateRoleBody }
  >({
    mutationFn: ({ id: roleId, body }) => updateRoleRequest(roleId, body),
    onSuccess: (response) => {
      const affected = response.affectedUserCount;
      const userWord = affected === 1 ? 'user' : 'users';
      const detail =
        affected > 0 ? `Role updated. ${String(affected)} ${userWord} affected.` : 'Role updated.';
      toast.success(detail);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role', id] });
      setPending(null);
      void navigate('/roles');
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not update role', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('roles.update')) {
    return <Navigate to="/roles" replace />;
  }

  function buildBody(values: RoleFormValues): UpdateRoleBody {
    return {
      name: values.name,
      description: values.description ?? null,
      permissionCodes: values.permissionCodes,
      isActive: values.isActive,
    };
  }

  function handleSubmit(values: RoleFormValues): void {
    setErrorMessage(null);
    const role = detailQuery.data;
    if (!role) return;
    const newSet = new Set(values.permissionCodes);
    const removing = role.permissionCodes.filter((c) => !newSet.has(c));
    if (removing.length > 0 && role.usersCount > 0) {
      // Stash values + open confirmation dialog. Don't fire the mutation yet.
      setPending({ values, removingCodes: removing });
      return;
    }
    updateMutation.mutate({ id, body: buildBody(values) });
  }

  function confirmRemoval(): void {
    if (pending === null) return;
    updateMutation.mutate({ id, body: buildBody(pending.values) });
  }

  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/roles" replace />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Role</h1>
        <p className="text-muted-foreground">Update the role and its permissions.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{detailQuery.data?.name ?? 'Role'}</CardTitle>
          <CardDescription>
            Removing permissions from a role with assigned users will require confirmation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {detailQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="role-edit-loading">
              Loading role…
            </p>
          )}
          {detailQuery.isError && (
            <p className="text-sm text-destructive" data-testid="role-edit-error">
              Failed to load role.
            </p>
          )}
          {detailQuery.data && (
            <RoleForm
              mode="edit"
              initialValues={{
                name: detailQuery.data.name,
                description: detailQuery.data.description,
                permissionCodes: [...detailQuery.data.permissionCodes],
                isActive: detailQuery.data.isActive,
              }}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              errorMessage={errorMessage}
            />
          )}
        </CardContent>
      </Card>
      <RemovePermissionsConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        removingCodes={pending?.removingCodes ?? []}
        affectedUserCount={detailQuery.data?.usersCount ?? 0}
        onConfirm={confirmRemoval}
        submitting={updateMutation.isPending}
      />
    </div>
  );
}
